/**
 * Features — Eaze Platform Capabilities
 *
 * Repositioned from accounting software to:
 * Africa's Business & Revenue Operating System
 *
 * Pillars:
 * - Finance & ERP Foundation
 * - Multi-Business Operations
 * - Revenue Assurance & Compliance
 * - AI & Business Intelligence
 */

import { motion } from "framer-motion";
import backgroundSvg from "@/assets/background.svg";
import Star from "@/assets/star.svg";
import Secure from "@/assets/dashboard/secure.png";
import Main from "@/assets/dashboard/main-dashboard.png";
import Report from "@/assets/dashboard/report.png";
import Security from "@/assets/icons/secure.svg";

import {
  CheckCircle2,
  Calculator,
  Building2,
  LineChart,
  Boxes,
  Users,
  Repeat,
  ShieldCheck,
  WifiOff,
} from "lucide-react";

const CAPABILITY_CHIPS = [
  { icon: Building2, label: "ERP Platform" },
  { icon: Calculator, label: "Finance & Accounting" },
  { icon: ShieldCheck, label: "Revenue Assurance" },
  { icon: LineChart, label: "AI Analytics" },
  { icon: Boxes, label: "Inventory & Procurement" },
  { icon: Users, label: "Multi-Business Management" },
  { icon: Repeat, label: "Workflow Automation" },
  { icon: WifiOff, label: "Offline First" },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Features = () => {
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src={backgroundSvg}
          alt="Background Pattern"
          className="h-full w-full object-cover"
        />
      </div>

      <div
        id="features"
        className="max-w-7xl mx-auto flex flex-col gap-12 min-h-screen px-6 lg:px-12 py-24"
      >
        {/* Header */}
        <motion.div
          {...fade}
          transition={{ duration: 0.5 }}
          className="flex flex-col gap-6 items-center"
        >
          <div className="border border-[#4CAF50] text-[#4CAF50] bg-[#4CAF50]/10 rounded-full px-5 py-2 flex items-center gap-2 w-fit">
            <img src={Star} alt="star" />
            <p className="text-sm font-semibold tracking-wide">
              PLATFORM CAPABILITIES
            </p>
          </div>

          <div className="flex flex-col gap-5 text-center max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight">
              Everything needed to run, manage and grow
              <span className="text-[#4CAF50]"> your business.</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Eaze combines ERP, Accounting, Procurement, Revenue Assurance,
              Compliance and Business Intelligence into a single operating
              system built for African businesses, enterprises and institutions.
            </p>
          </div>
        </motion.div>

        {/* Capability Chips */}
        <motion.div
          {...fade}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {CAPABILITY_CHIPS.map(({ icon: Icon, label }, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm px-4 py-4 hover:border-[#4CAF50]/40 hover:shadow-md transition-all"
            >
              <div className="rounded-lg bg-[#4CAF50]/10 p-2 text-[#4CAF50]">
                <Icon className="h-5 w-5" />
              </div>

              <span className="text-sm font-medium text-gray-700">{label}</span>
            </div>
          ))}
        </motion.div>

        {/* Feature Blocks */}
        <div className="flex flex-col gap-28 py-10">
          {/* Finance & ERP */}
          <motion.div
            {...fade}
            transition={{ duration: 0.55 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center"
          >
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <img src={Security} alt="" className="w-14 h-14" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  Finance & ERP Foundation
                </h2>
              </div>

              <p className="text-lg text-gray-600">
                Manage accounting, procurement, payroll, inventory and financial
                reporting from a single source of truth.
              </p>

              <div className="space-y-3">
                {[
                  "Double-entry accounting engine",
                  "P&L, Balance Sheet and Cash Flow",
                  "Procurement & purchase management",
                  "Payroll and financial controls",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#4CAF50]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#4CAF50]/10 rounded-3xl p-6 border border-[#4CAF50]/15">
              <img
                src={Report}
                alt="Financial Reports"
                className="rounded-xl"
              />
            </div>
          </motion.div>

          {/* Multi-Business */}
          <motion.div
            {...fade}
            transition={{ duration: 0.55 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center"
          >
            <div className="bg-[#4CAF50]/10 rounded-3xl p-6 border border-[#4CAF50]/15 max-lg:order-2">
              <img src={Main} alt="Dashboard" className="rounded-xl" />
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <img src={Security} alt="" className="w-14 h-14" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  Multi-Business Operations
                </h2>
              </div>

              <p className="text-lg text-gray-600">
                Operate multiple businesses, branches and teams from a
                centralized platform with real-time visibility.
              </p>

              <div className="space-y-3">
                {[
                  "Multi-company management",
                  "Branch performance tracking",
                  "Executive dashboards",
                  "Role-based access controls",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#4CAF50]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Revenue Assurance */}
          <motion.div
            {...fade}
            transition={{ duration: 0.55 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center"
          >
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <img src={Security} alt="" className="w-14 h-14" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  Revenue Assurance & Compliance
                </h2>
              </div>

              <p className="text-lg text-gray-600">
                Protect revenue, enforce controls and monitor compliance through
                automated workflows and audit-ready reporting.
              </p>

              <div className="space-y-3">
                {[
                  "Audit trails and approvals",
                  "Revenue monitoring",
                  "Compliance workflows",
                  "Tax-ready reporting",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#4CAF50]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#4CAF50]/10 rounded-3xl p-6 border border-[#4CAF50]/15">
              <img
                src={Secure}
                alt="Revenue Assurance"
                className="rounded-xl"
              />
            </div>
          </motion.div>

          {/* AI & BI */}
          <motion.div
            {...fade}
            transition={{ duration: 0.55 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center"
          >
            <div className="bg-[#4CAF50]/10 rounded-3xl p-6 border border-[#4CAF50]/15 max-lg:order-2">
              <img src={Main} alt="AI Analytics" className="rounded-xl" />
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <img src={Security} alt="" className="w-14 h-14" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  AI & Business Intelligence
                </h2>
              </div>

              <p className="text-lg text-gray-600">
                Turn business data into insights with dashboards, analytics and
                future-ready AI capabilities.
              </p>

              <div className="space-y-3">
                {[
                  "Executive dashboards",
                  "Performance analytics",
                  "Revenue forecasting",
                  "AI-ready architecture",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#4CAF50]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Features;
